import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { Client, Room } from 'colyseus.js';
import {
  TurnResolution,
  RunAnomaly,
  DeckCard,
  ScoreBreakdown,
  ModifierCard,
  RunShopState,
  JokerDefinition,
  ConsumableDefinition,
} from '@solante/shared';
import { toast } from 'sonner';

export type PracticeDifficulty = 'easy' | 'medium' | 'hard';

type ClientPlayerRunState = {
  chips: number;
  hearts: number;
  totalScore: number;
  jokerSlots: number;
  jokers: JokerDefinition[];
  consumables: ConsumableDefinition[];
  consumablesUsed: string[];
  tempoStreak: number;
  phase: 'play' | 'shop';
  nextShopTurn: number;
  shopGeneration: number;
  shopRerolls: number;
};

export interface PracticeMatchState {
  matchId: string;
  status: 'waiting' | 'inProgress' | 'completed';
  difficulty: PracticeDifficulty;
  targetScore: number;
  players: Array<{
    id: string;
    displayName: string;
    isAi: boolean;
    connected: boolean;
  }>;
  currentTurnId: string;
  timeline: TurnResolution[];
  currentHand?: DeckCard[];
  currentHandOwner?: string;
  currentHandSequence?: number;
  activeAnomaly?: RunAnomaly | null;
  nextAnomaly?: RunAnomaly | null;
  anomalyHistory?: RunAnomaly[];
  deckStates?: Record<string, { drawPile: string[]; discardPile: string[] }>;
  turnDeadlineIso?: string;
  turnSecondsRemaining?: number;
  playerRunStates?: Record<string, ClientPlayerRunState>;
  playerShopStates?: Record<string, RunShopState>;
  phase?: 'play' | 'shop';
  winner?: string;
  winReason?: string;
}

interface PracticeMatchStore {
  state?: PracticeMatchState;
  connecting: boolean;
  error?: Error;
  setState: (state?: PracticeMatchState) => void;
  setStatus: (status: Partial<Pick<PracticeMatchStore, 'connecting' | 'error'>>) => void;
}

const usePracticeMatchStoreBase = create<PracticeMatchStore>((set) => ({
  connecting: false,
  setState: (state) => set({ state }),
  setStatus: (status) => set(status),
}));

let activeRoom: Room | undefined;
let activeClient: Client | undefined;

type ForEachable<T> = { forEach: (callback: (item: T) => void) => void };

type RawPlayer = {
  id?: string;
  displayName?: string;
  isAi?: boolean;
  connected?: boolean;
};

type RawTimelineEntry = {
  sequence?: number;
  player?: string;
  cardsJson?: string;
  modifiersJson?: string;
  breakdownJson?: string;
};

const safeParseJson = <T>(raw: unknown, fallback: T): T => {
  if (typeof raw !== 'string' || !raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

export const usePracticeMatchConnection = () => {
  const setState = usePracticeMatchStoreBase((s) => s.setState);
  const { connecting, error } = usePracticeMatchStoreBase((s) => ({
    connecting: s.connecting,
    error: s.error,
  }));
  const setStatus = usePracticeMatchStoreBase((s) => s.setStatus);

  const connect = useCallback(
    async (options: {
      difficulty: PracticeDifficulty;
      startingJokerId?: string | null;
      disableTimer?: boolean;
    }) => {
      const endpoint = import.meta.env.VITE_WS_ENDPOINT || 'ws://localhost:8787';

      // Clean up existing connection
      if (activeRoom) {
        try {
          await activeRoom.leave(true);
        } catch {
          // Ignore errors during cleanup
        }
        activeRoom = undefined;
      }

      try {
        setStatus({ connecting: true, error: undefined });

        if (!activeClient) {
          activeClient = new Client(endpoint);
        }

        const room = await activeClient.create('practice', {
          difficulty: options.difficulty,
          startingJokerId: options.startingJokerId,
          disableTimer: options.disableTimer,
        });

        activeRoom = room;

        const handleStateChange = (rawState: unknown) => {
          const state = rawState as {
            matchId?: string;
            status?: string;
            difficulty?: string;
            targetScore?: number;
            currentTurnId?: string;
            players?: ForEachable<RawPlayer>;
            timeline?: ForEachable<RawTimelineEntry>;
            activeAnomalyJson?: string;
            nextAnomalyJson?: string;
            anomalyHistoryJson?: string;
            currentHandJson?: string;
            currentHandOwner?: string;
            currentHandSequence?: number;
            deckStatesJson?: string;
            turnDeadlineIso?: string;
            turnSecondsRemaining?: number;
            playerRunStatesJson?: string;
            playerShopStatesJson?: string;
            phase?: string;
            winner?: string;
            winReason?: string;
          };

          const players: PracticeMatchState['players'] = [];
          if (state.players && typeof state.players.forEach === 'function') {
            state.players.forEach((player) => {
              if (!player || typeof player.id !== 'string') return;
              players.push({
                id: player.id,
                displayName: typeof player.displayName === 'string' ? player.displayName : '',
                isAi: player.isAi === true,
                connected: player.connected !== false,
              });
            });
          }

          const timeline: TurnResolution[] = [];
          if (state.timeline && typeof state.timeline.forEach === 'function') {
            state.timeline.forEach((turn) => {
              if (!turn) return;
              const breakdown = safeParseJson<ScoreBreakdown | null>(turn.breakdownJson, null);
              const player = typeof turn.player === 'string' ? turn.player : '';
              if (!player || !breakdown) return;
              const sequence =
                typeof turn.sequence === 'number' ? turn.sequence : timeline.length + 1;
              timeline.push({
                sequence,
                player,
                cardsPlayed: safeParseJson<DeckCard[]>(turn.cardsJson, []),
                modifiers: safeParseJson<ModifierCard[]>(turn.modifiersJson, []),
                breakdown,
              });
            });
          }

          let activeAnomaly: RunAnomaly | null = null;
          if (state.activeAnomalyJson) {
            try {
              activeAnomaly = JSON.parse(state.activeAnomalyJson) as RunAnomaly;
            } catch {
              // Ignore parse errors
            }
          }

          let nextAnomaly: RunAnomaly | null = null;
          if (state.nextAnomalyJson) {
            try {
              nextAnomaly = JSON.parse(state.nextAnomalyJson) as RunAnomaly;
            } catch {
              // Ignore parse errors
            }
          }

          let anomalyHistory: RunAnomaly[] | undefined;
          if (state.anomalyHistoryJson) {
            try {
              anomalyHistory = JSON.parse(state.anomalyHistoryJson) as RunAnomaly[];
            } catch {
              // Ignore parse errors
            }
          }

          let deckStates: Record<string, { drawPile: string[]; discardPile: string[] }> | undefined;
          if (state.deckStatesJson) {
            try {
              deckStates = JSON.parse(state.deckStatesJson);
            } catch {
              // Ignore parse errors
            }
          }

          let parsedHand: DeckCard[] | undefined;
          if (state.currentHandJson) {
            try {
              parsedHand = JSON.parse(state.currentHandJson) as DeckCard[];
            } catch {
              // Ignore parse errors
            }
          }

          const currentHandOwner = state.currentHandOwner || undefined;
          const currentHandSequence = state.currentHandSequence ?? undefined;
          const currentHand = currentHandOwner === 'player' ? (parsedHand ?? []) : undefined;

          const playerRunStates = state.playerRunStatesJson
            ? safeParseJson<Record<string, ClientPlayerRunState>>(state.playerRunStatesJson, {})
            : undefined;

          const playerShopStates = state.playerShopStatesJson
            ? safeParseJson<Record<string, RunShopState>>(state.playerShopStatesJson, {})
            : undefined;

          const phase = state.phase === 'shop' ? 'shop' : 'play';

          setState({
            matchId: state.matchId ?? '',
            status: (state.status as PracticeMatchState['status']) ?? 'waiting',
            difficulty: (state.difficulty as PracticeDifficulty) ?? 'medium',
            targetScore: state.targetScore ?? 1000,
            players,
            currentTurnId: state.currentTurnId ?? '',
            timeline,
            currentHand,
            currentHandOwner,
            currentHandSequence,
            activeAnomaly,
            nextAnomaly,
            anomalyHistory,
            deckStates,
            turnDeadlineIso: state.turnDeadlineIso,
            turnSecondsRemaining: state.turnSecondsRemaining,
            playerRunStates,
            playerShopStates,
            phase,
            winner: state.winner,
            winReason: state.winReason,
          });
        };

        room.onStateChange(handleStateChange);
        handleStateChange(room.state);

        room.onMessage('event', (rawEvent: unknown) => {
          if (!rawEvent || typeof rawEvent !== 'object') return;
          const event = rawEvent as { type?: string; [key: string]: unknown };

          switch (event.type) {
            case 'match:started':
              toast.success('Practice match started!');
              break;
            case 'hand:dealt': {
              const cards = Array.isArray(event.cards)
                ? (event.cards as DeckCard[]).map((card) => ({ ...card }))
                : [];
              const sequence = typeof event.sequence === 'number' ? event.sequence : undefined;
              if (cards.length) {
                const snapshot = usePracticeMatchStoreBase.getState().state;
                if (snapshot) {
                  usePracticeMatchStoreBase.getState().setState({
                    ...snapshot,
                    currentHandOwner: 'player',
                    currentHandSequence: sequence ?? snapshot.currentHandSequence,
                    currentHand: cards,
                  });
                }
              }
              break;
            }
            case 'hand:cleared': {
              const snapshot = usePracticeMatchStoreBase.getState().state;
              if (snapshot && snapshot.currentHandOwner === 'player') {
                usePracticeMatchStoreBase.getState().setState({
                  ...snapshot,
                  currentHand: [],
                  currentHandOwner: undefined,
                });
              }
              break;
            }
            case 'turn:timeout': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              if (playerId === 'player') {
                toast.error('Turn timeout!');
              }
              break;
            }
            case 'shop:opened':
              toast('Shop phase active!');
              break;
            case 'shop:closed':
              toast('Shop phase complete.');
              break;
            case 'shop:ready': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              if (playerId === 'ai') {
                toast.info('AI is ready.');
              }
              break;
            }
            case 'shop:buy': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              if (playerId === 'ai') {
                const applied = event.applied as { kind?: string; id?: string } | undefined;
                if (applied?.kind) {
                  toast.info(`AI bought a ${applied.kind}.`);
                }
              }
              break;
            }
            case 'shop:reroll': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              if (playerId === 'ai') {
                toast.info('AI rerolled the shop.');
              }
              break;
            }
            case 'anomaly:activated': {
              const anomaly = event.anomaly as { description?: string };
              if (anomaly?.description) {
                toast.warning(`Anomaly: ${anomaly.description}`);
              }
              break;
            }
            case 'anomaly:expired': {
              const anomaly = event.anomaly as { description?: string };
              if (anomaly?.description) {
                toast(`Anomaly expired: ${anomaly.description}`);
              }
              break;
            }
            case 'run:resource': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              const chipsDelta = typeof event.chipsDelta === 'number' ? event.chipsDelta : 0;
              const heartsDelta = typeof event.heartsDelta === 'number' ? event.heartsDelta : 0;
              if (playerId === 'player') {
                const parts: string[] = [];
                if (chipsDelta) parts.push(`${chipsDelta > 0 ? '+' : ''}${chipsDelta} chips`);
                if (heartsDelta) parts.push(`${heartsDelta > 0 ? '+' : ''}${heartsDelta} hearts`);
                if (parts.length) toast(`${parts.join(' / ')}`);
              }
              break;
            }
            case 'hearts:damaged': {
              const playerId = typeof event.playerId === 'string' ? event.playerId : '';
              const amount = typeof event.amount === 'number' ? event.amount : 1;
              if (playerId === 'player') {
                toast.error(`You lost ${amount} heart${amount > 1 ? 's' : ''}!`);
              } else {
                toast.success(`AI lost ${amount} heart${amount > 1 ? 's' : ''}!`);
              }
              break;
            }
            case 'match:ended': {
              const playerWon = event.playerWon === true;
              const reason = typeof event.reason === 'string' ? event.reason : '';
              if (playerWon) {
                toast.success(`You won! (${reason})`);
              } else {
                toast.error(`You lost. (${reason})`);
              }
              break;
            }
            default:
              break;
          }
        });

        room.onLeave((code) => {
          if (activeRoom === room) {
            activeRoom = undefined;
            if (code !== 1000) {
              toast.warning('Disconnected from practice room.');
            }
          }
        });

        room.onError((code, message) => {
          toast.error(`Room error ${code}: ${message}`);
          setStatus({ error: new Error(message) });
        });

        setStatus({ connecting: false, error: undefined });
      } catch (err) {
        setStatus({ connecting: false, error: err as Error });
      }
    },
    [setState, setStatus],
  );

  const signalReady = useCallback(async () => {
    if (!activeRoom) throw new Error('Room not connected');
    activeRoom.send('ready');
  }, []);

  const playTurn = useCallback(async (payload: { playedCardIds: string[] }) => {
    if (!activeRoom) throw new Error('Room not connected');
    const currentState = usePracticeMatchStoreBase.getState().state;
    const sequence = currentState?.currentHandSequence ?? currentState?.timeline?.length ?? 0;
    activeRoom.send('turn', { sequence, payload });
  }, []);

  const purchaseOffer = useCallback(async (offerId: string) => {
    if (!activeRoom) throw new Error('Room not connected');
    activeRoom.send('shop', { type: 'buy', offerId });
  }, []);

  const rerollShop = useCallback(async () => {
    if (!activeRoom) throw new Error('Room not connected');
    activeRoom.send('shop', { type: 'reroll' });
  }, []);

  const signalShopReady = useCallback(async () => {
    if (!activeRoom) throw new Error('Room not connected');
    activeRoom.send('shop', { type: 'ready' });
  }, []);

  const forfeit = useCallback(async () => {
    if (!activeRoom) throw new Error('Room not connected');
    activeRoom.send('forfeit');
  }, []);

  const disconnect = useCallback(async () => {
    if (activeRoom) {
      try {
        await activeRoom.leave(true);
      } catch {
        // Ignore errors during disconnect
      }
      activeRoom = undefined;
    }
    setState(undefined);
  }, [setState]);

  const state = usePracticeMatchStoreBase((s) => s.state);

  return {
    connect,
    disconnect,
    signalReady,
    state,
    connecting,
    error,
    playTurn,
    purchaseOffer,
    rerollShop,
    signalShopReady,
    forfeit,
  };
};
